<template>
    <section>
        <b-steps
            v-model="activeStep"
            :rounded="false"
            size="is-small"
            :has-navigation="false"
            mobile-mode="minimalist">

            <b-step-item step="1">
                <h1 class="pt-2 pb-4 title has-text-centered">About you</h1>
                <section class="has-text-centered" grouped>
                    <b-field label="Gender" 
                        custom-class="pb-0 has-text-grey-light is-uppercase" 
                        position="is-centered" >
                        <b-switch size="is-large" class="ml-51"
                            v-model="form.gender"
                            :rounded="false"
                            :left-label="true"
                            passive-type='is-primary'>Male
                        </b-switch>
                        <label class="switch-duplicate" @click="form.gender = !form.gender;">Female</label>
                    </b-field>
                </section>
                <section class="has-text-centered mt-5" grouped>
                    <b-field label="Your age" 
                        custom-class="has-text-grey-light is-uppercase" 
                        position="is-centered" >
                        <b-slider v-model="form.age" :min="10" :max="100"></b-slider>
                    </b-field>
                    <p class="has-text-grey">{{form.age}} years old</p>
                </section>
            </b-step-item>

            <b-step-item step="2">
                <h1 class="pt-2 pb-4 title has-text-centered">Activity</h1>
                <section>
                    <b-field>
                        <b-radio v-model="form.activity"
                            size="is-medium"
                            native-value="1">
                            Sedentary
                        </b-radio>
                    </b-field>           
                    <b-field>
                        <b-radio v-model="form.activity"
                            size="is-medium"
                            native-value="2">
                            Light activity (1-3 days/week)
                        </b-radio>
                    </b-field>
                    <b-field>
                        <b-radio v-model="form.activity"
                            size="is-medium"
                            native-value="3">
                            Medium activity (3-5 days/week)
                        </b-radio>
                    </b-field>
                    <b-field>
                        <b-radio v-model="form.activity"
                            size="is-medium"
                            native-value="4">
                            Intense activity (7 days/week)
                        </b-radio>
                    </b-field>
                </section>
            </b-step-item>

            <b-step-item step="3">
                <h1 class="pt-2 pb-4 title has-text-centered">Measurements</h1>
                <section>
                    <div class="columns is-mobile">
                        <div class="column">
                            <b-radio-button v-model="form.units"
                                native-value="0"
                                type="is-primary"
                                size="is-medium">
                                <span>Metric</span>
                            </b-radio-button>
                        </div>
                        <div class="column">
                            <b-radio-button v-model="form.units"
                                native-value="1"
                                type="is-primary"
                                size="is-medium">
                                <span>Imperial</span>
                            </b-radio-button>
                        </div>
                    </div>

                    <template v-if="form.units == 0">
                        <b-field label="Height - cm" custom-class="has-text-grey-light is-uppercase">
                            <b-input v-model="form.height" placeholder="Height"></b-input>
                            <p class="help is-danger" v-if="$v.form.height && $v.form.height.$error"> 
                                Please enter a valid height!
                            </p>
                        </b-field>
                        <b-field label="Weight - kg" custom-class="has-text-grey-light is-uppercase">
                            <b-input v-model="form.weight" placeholder="Weight"></b-input>
                            <p class="help is-danger" v-if="$v.form.weight && $v.form.weight.$error"> 
                                Please enter a valid weight!
                            </p>
                        </b-field>
                        <b-field label="Target Weight - kg" custom-class="has-text-grey-light is-uppercase">
                            <b-input v-model="form.target_weight" placeholder="Target weight"></b-input>
                            <p class="help is-danger" v-if="$v.form.target_weight && $v.form.target_weight.$error"> 
                                Please enter a valid target weight!
                            </p>
                        </b-field>
                    </template>
                    <template v-if="form.units == 1">
                        <div class="columns is-mobile mb-0">
                            <div class="column">
                                <b-field label="Feet" custom-class="has-text-grey-light is-uppercase">
                                    <b-input v-model="form.feet" placeholder="Feet"></b-input>
                                    <p class="help is-danger" v-if="$v.form.feet && $v.form.feet.$error"> 
                                        Please enter a valid height in feet!
                                    </p>
                                </b-field>
                            </div>
                            <div class="column">
                                <b-field label="Inches" custom-class="has-text-grey-light is-uppercase">
                                    <b-input v-model="form.inches" placeholder="Inches"></b-input>
                                    <p class="help is-danger" v-if="$v.form.inches && $v.form.inches.$error">
                                        Please enter a valid height in inches!
                                    </p>
                                </b-field>
                            </div>
                        </div>       
                        <b-field label="Weight - pounds" custom-class="has-text-grey-light is-uppercase">
                            <b-input v-model="form.weight" placeholder="Weight"></b-input>
                            <p class="help is-danger" v-if="$v.form.weight && $v.form.weight.$error"> 
                                Please enter a valid weight!
                            </p>
                        </b-field>
                        <b-field label="Target weight - pounds" custom-class="has-text-grey-light is-uppercase">
                            <b-input v-model="form.target_weight" placeholder="Target weight"></b-input>
                            <p class="help is-danger" v-if="$v.form.target_weight && $v.form.target_weight.$error">
                                Please enter a valid target weight!
                            </p>
                        </b-field>
                    </template>
                </section>
            </b-step-item>

            <b-step-item step="4">
                <h1 class="pt-2 pb-0 mb-1 title has-text-centered">Results</h1>
                <p class="has-text-justified has-text-grey-light pb-5">Give us some information so that we can send you your information.</p>
                <section v-if="!showResults">

                        <b-field label="Name" custom-class="has-text-grey-light is-uppercase">
                            <b-input v-model="form.name"></b-input>
                            <p class="help is-danger" v-if="$v.form.name && $v.form.name.$error"> 
                                Please enter a valid name!
                            </p> 
                        </b-field>
                        <b-field label="Email" custom-class="has-text-grey-light is-uppercase">
                            <b-input v-model="form.email"></b-input>
                            <p class="help is-danger" v-if="$v.form.email && $v.form.email.$error"> 
                                Please enter a valid email!
                            </p>
                        </b-field> 
                        <b-field class="has-text-centered mt-5">
                            <b-button type="is-primary" size="is-medium" expanded @click="onResults">Show results</b-button>
                        </b-field>

                </section>

                <section v-if="showResults">
                    

<div class="columns mt-5">
    <div class="column is-full">        
        <nav class="level">
            <div class="level-item has-text-centered">
                <div>
                    <p class="heading">
                        <b-tag type="is-success">
                            Daily calories
                        </b-tag>
                    </p>
                  <p class="title">{{recommended.intake}}</p>
                </div>
            </div>
        </nav>
    </div>
</div>


<div class="columns mt-5">
    <div class="column is-full">        
        <nav class="level">
            <div class="level-item has-text-centered">
                <div>
                    <p class="heading">
                        <b-tag type="is-success">
                            Daily fats between
                        </b-tag>
                    </p>                    
                  <p class="title">{{recommended.fats.min|to_oz(this.form.units)}} - {{recommended.fats.max|to_oz(this.form.units)}}</p>
                </div>
            </div>
        </nav>
    </div>    
</div>
      
<div class="columns mt-5">
    <div class="column is-full">        
        <nav class="level">
            <div class="level-item has-text-centered">
                <div>
                    <p class="heading">
                        <b-tag type="is-success">
                            Daily protein
                        </b-tag>
                    </p>                    
                  <p class="title">{{recommended.protein.min|to_oz(this.form.units)}} - {{recommended.protein.max|to_oz(this.form.units)}}</p>
                </div>
            </div>
        </nav>
    </div>    
</div>

<div class="columns mt-5">
    <div class="column is-full">        
        <nav class="level">
            <div class="level-item has-text-centered">
                <div>
                    <p class="heading">
                        <b-tag type="is-danger">
                            Daily carbs
                        </b-tag>
                    </p>                    
                  <p class="title">{{recommended.carbs.min|to_oz(this.form.units)}} - {{recommended.carbs.max|to_oz(this.form.units)}}</p>
                </div>
            </div>
        </nav>
    </div>    
</div>

<div class="columns mt-5">
    <div class="column is-full">        
        <nav class="level">
            <div class="level-item has-text-centered">
                <div>
                    <p class="heading">
                        <b-tag type="is-info">
                            Daily water intake
                        </b-tag>
                    </p>                    
                  <p class="title">{{recommended.water}} l</p>
                </div>
            </div>
        </nav>
    </div>    
</div>

<div class="columns mt-5">
    <div class="column is-full">        
        <nav class="level">
            <div class="level-item has-text-centered">
                <div>
                    <p class="heading">
                        <b-tag type="is-info">
                            Body-mass index
                        </b-tag>
                    </p>                    
                    <p class="title">
                        {{recommended.bmi}}
                        <span v-if="recommended.bmi > 25"> overweight</span>
                        <span v-if="recommended.bmi <= 25 && recommended.bmi >= 18.5"> normal weight</span>
                        <span v-if="recommended.bmi < 18.5" class="is-size-5 tag"> underweight</span>                        
                    </p>
                </div>
            </div>
        </nav>
    </div>    
</div>

<div class="columns mt-5">
    <div class="column is-full">        
        <nav class="level">
            <div class="level-item has-text-centered">
                <div>
                    <p class="heading">
                        <b-tag type="is-info">
                            Ideal weight
                        </b-tag>
                    </p>                    
                    <p class="title">
                        {{recommended.ideal_weight|to_pounds(this.form.units)}}
                    </p>
                </div>
            </div>
        </nav>
    </div>    
</div>
                </section>
            </b-step-item>

            <template v-if="activeStep != 5 && !showResults"
                #navigation="{previous, next}">
                <div class="block has-text-centered mt-3" >
                    <b-button
                        type="is-primary is-light"
                        icon-left="arrow-left"
                        :disabled="previous.disabled"
                        class="mr-2"
                        @click.prevent="prevInternal(previous.action)">
                    </b-button>
                    <b-button
                        type="is-primary is-light"
                        icon-right="arrow-right"
                        :disabled="next.disabled"
                        class="ml-2"
                        @click.prevent="nextInternal(next.action)">
                    </b-button>
                </div>
            </template>
        </b-steps>
    </section>
</template>

<script>

    import { validationMixin } from "vuelidate";
    import { required, minLength, maxLength, decimal, minValue, email } from "vuelidate/lib/validators";
    
    export default {
        mixins: [validationMixin],
        data() {
            return {
                activeStep: 0,
                showResults : false,
                form:
                {
                    gender : false,
                    age : 20,
                    activity : 2,
                    units: "0",
                    weight: null,
                    target_weight: 0,
                    height : null,
                    feet: null,
                    inches: null,
                    name : "",
                    email: "",
                    token: null,
                    lang: 'en'
                },
                w: null,
                h: null,
                recommended : {
                    intake : 0,
                    fats: {
                        min : 1,
                        max : 101
                    },
                    carbs: {
                        min: 2,
                        max : 102
                    },
                    protein: {
                        min : 3,
                        max : 103
                    },
                    bmi : 1,
                    water : 0.5,
                    ideal_weight: 0
                }
            }
        },
        validations() {
            if (2 == this.activeStep)
            {
                if (0 == this.form.units)
                {
                    return {
                        form : {
                            height : {
                                required,
                                decimal,
                                minValue: minValue(1)
                            },
                            weight : {
                                required,
                                decimal,
                                minValue: minValue(1)
                            },
                            target_weight : {
                                decimal,
                                minValue: minValue(1)
                            }
                        }
                    };
                }else
                {
                    return {
                        form : {
                            feet : {
                                required,
                                decimal,
                                minValue: minValue(1)
                            },
                            inches : {
                                required,
                                decimal,
                                minValue: minValue(1)
                            }, 
                            weight : {
                                required,
                                decimal,
                                minValue: minValue(1)
                            },
                            target_weight : {
                                decimal,
                                minValue: minValue(1)
                            }
                        }
                    };
                }

            }else
            if (3 == this.activeStep)
            {
                return {
                    form : {
                        name: {
                            required,
                            minLength: minLength(2),
                            maxLength: maxLength(150)
                        },
                        email: {
                            required,
                            email,
                            minLength: minLength(2),
                            maxLength: maxLength(150)
                        }
                    }    
                };
            }      

            return { 
                form: {

                }
            };
        },
        mounted()
        {
            this.getVars();
        },
        filters: {
            to_oz: function (value, metric) 
            {
                if (metric == "1")
                {
                    return Math.round(value * 0.0352) + " oz";
                }

                return Math.round(value) + " g";
            },
            to_pounds(value, metric)
            {
                if (metric == "1")
                {
                    return Math.round(value * 2.2) + " lbs";
                }

                return Math.round(value) + " kg";                
            }
        },                
        methods:
        {
            getVars()
            {
                var Params = new Object ();
                let query = window.location.href;
                var Pairs = query.split(/[?;&]/);
                for ( var i = 0; i < Pairs.length; i++ ) {
                    var KeyVal = Pairs[i].split('=');
                    if ( ! KeyVal || KeyVal.length != 2 ) continue;
                    var key = unescape( KeyVal[0] );
                    var val = unescape( KeyVal[1] );
                    val = val.replace(/\+/g, ' ');
                    Params[key] = val;
                }
                this.form.token = Params['hash'];
                this.form.lang = Params['lang'];
            },
            prevInternal(defaultPrev)
            {
                let bContinue = true;

                this.$v.form.$touch();

                if (this.$v.form.$invalid) 
                { 
                    bContinue = false;
                }
                
                if (bContinue)
                {
                    defaultPrev();
                }
            },
            nextInternal(defaultNext)
            {
                let bContinue = true;

                this.$v.form.$touch();

                if (this.$v.form.$invalid) 
                { 
                    bContinue = false;
                }

                if (bContinue)
                {
                    defaultNext();
                }
            },
            onResults()
            {
                let bContinue = true;

                this.$v.form.$touch();

                if (this.$v.form.$invalid) 
                { 
                    bContinue = false;
                }                  

                if (bContinue)
                {
                    this.calculate();
                    this.createLead();

                    this.showResults = true;
                }                
            },
            calculate()
            {
                this.toMetric();
                this.bmi();
                this.water();
                this.calories();

            },
            createLead()
            {
                const leadData = JSON.stringify({formData: this.form});

                this.sendByXhr(leadData)
                .then(function(response) {
                    //console.log(response);
                })
                .catch(function(err) {
                    console.error("There was an error!", err.statusText);
                });
            },
            sendByXhr(data) {
                return new Promise(function(resolve, reject) {
                    const xhr = new XMLHttpRequest();
                    xhr.open("POST", "https://app.nutritioapp.com/add_lead");
                    xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
                    xhr.onload = function() {
                    if (this.status >= 200 && this.status < 300) {
                        resolve(xhr.response);
                    } else {
                        reject({
                        status: this.status,
                        statusText: xhr.statusText,
                        });
                    }
                    };
                    xhr.onerror = function() {
                    reject({
                        status: this.status,
                        statusText: xhr.statusText,
                    });
                    };
                    xhr.send(data);
                });
            },
            toMetric() 
            {
                if ( this.form.units == 0 ) 
                {
                    this.w = parseFloat(this.form.weight, 1);
                    this.h = parseFloat(this.form.height, 1);
                } else 
                if ( this.form.units == 1 ) 
                {
                    const height = this.form.feet / 0.032808 + this.form.inches / 0.3937;
                    const weight = this.form.weight * 0.45359237;
                    
                    this.h = height.toFixed(1);
                    this.w = weight.toFixed(1);
                }
            },
            water()
            {
                this.recommended.water = (this.w * 0.033).toFixed(1);
            },
            bmi() 
            {
                this.recommended.bmi = ((this.w / (this.h * this.h)) * 10000).toFixed(1);
            },
            calories() 
            {
                let calories = 0;
                let ideal_weight = 50 + 0.75 * (this.h - 150) + 0.25 * (this.form.age - 20);

                if (this.form.gender) { //F
                    ideal_weight = ideal_weight * 0.9;
                    calories = 10 * ideal_weight + 6.25 * this.h - 5 * this.form.age - 161;
                } else {
                    calories = 10 * ideal_weight + 6.25 * this.h - 5 * this.form.age + 5;
                }
                this.recommended.ideal_weight = Math.round(ideal_weight);

                switch (parseInt(this.form.activity)) {
                    case 1:
                        calories *= 1.2;
                        break;
                    case 2:
                        calories *= 1.375;
                        break;
                    case 3:
                        calories *= 1.55;
                        break;
                    case 4:
                        calories *= 1.725;
                        break;
                }

                this.recommended.intake = Math.round(calories);

                //protein
                const protein = Math.round(calories / 4.1);
                this.recommended.protein.min = Math.round(protein * 10 /100);
                this.recommended.protein.max = Math.round(protein * 35 /100);

                //carbs
                const carbs = Math.round(calories / 4.1);
                this.recommended.carbs.min = Math.round(carbs * 45 /100);
                this.recommended.carbs.max = Math.round(carbs * 60 /100);

                //fats
                const fats = Math.round(calories / 9.3);
                this.recommended.fats.min = Math.round(protein * 20 /100);
                this.recommended.fats.max = Math.round(protein * 35 /100);
            }
        }
    };
</script>


<style lang="css">
    nav.step-navigation
    {
        text-align: center!important;
    }

    label.switch-duplicate
    {
        font-size: 24px; 
        line-height: 36px;
        cursor: pointer;
    }
    p.heading span.tag
    {
        width: 20em;
        margin-bottom: 0.5em;
    }
</style>